import React, { useState, useEffect } from 'react';
import { Star, Quote, CheckCircle2, MapPin } from 'lucide-react';
import { TESTIMONIALS } from '../../mock';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../ui/carousel";

export default function Testimonials() {
  const [api, setApi] = useState(null);

  useEffect(() => {
    if (!api) return;
    
    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 7000);

    return () => clearInterval(interval);
  }, [api]);

  return (
    <section className="bg-brand-grey py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-orange/10 text-brand-orange rounded-full text-[12px] font-bold uppercase tracking-wider mb-3">
              <Quote size={14} /> Reviews
            </div>
            <h2 className="font-poppins text-[28px] sm:text-[36px] lg:text-[48px] text-brand-blue font-extrabold leading-[1.1]">Real travelers. <span className="text-brand-orange">Real memories.</span></h2>
            <div className="flex items-center gap-4 mt-5">
              <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} size={20} className="text-brand-orange fill-brand-orange" />)}</div>
              <div className="h-8 w-px bg-brand-blue/15" />
              <div>
                <span className="text-brand-blue font-bold text-lg">4.8/5</span>
                <span className="text-brand-blue/50 text-sm ml-2 font-medium">from 2,100+ verified reviews</span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative group/carousel">
          <Carousel 
            setApi={setApi}
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4 md:-ml-6">
              {TESTIMONIALS.map((t, i) => (
                <CarouselItem key={t.id} className="pl-4 md:pl-6 basis-full sm:basis-1/2 lg:basis-1/3 py-2">
                  <div className="bg-white rounded-[2.5rem] p-8 lg:p-10 shadow-premium border border-brand-greyDark flex flex-col h-full relative overflow-hidden group/card transition-all duration-500 hover:border-brand-orange/20">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-brand-orange/5 rounded-bl-[100px] flex items-start justify-end p-6">
                      <Quote size={32} className="text-brand-orange/15 group-hover/card:scale-110 transition-transform" />
                    </div>
                    
                    <div className="flex mb-6">{[...Array(t.rating)].map((_,j) => <Star key={j} size={15} className="text-brand-orange fill-brand-orange" />)}</div>
                    
                    <p className="text-brand-blue/80 text-[16px] lg:text-[17px] leading-relaxed font-medium mb-10 flex-grow italic">“{t.text}”</p>
                    
                    <div className="flex items-center gap-4 pt-8 border-t border-brand-grey">
                      <div className="relative">
                        <img src={t.avatar} alt={t.name} className="w-14 h-14 rounded-2xl object-cover shadow-sm group-hover/card:scale-105 transition-transform" />
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                          <CheckCircle2 size={10} className="text-white" fill="currentColor"/>
                        </div>
                      </div>
                      <div>
                        <div className="font-poppins font-bold text-brand-blue text-[15px]">{t.name}</div>
                        <div className="flex items-center gap-1.5 text-[12px] text-brand-blue/50 font-semibold uppercase tracking-wider">
                          <MapPin size={11} className="text-brand-orange"/> {t.destination}
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            {/* Desktop Navigation Arrows (Floating) */}
            <CarouselPrevious className="hidden xl:flex absolute -left-16 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full border border-brand-greyDark shadow-xl bg-white text-brand-blue hover:bg-brand-orange hover:text-white transition-all z-10" />
            <CarouselNext className="hidden xl:flex absolute -right-16 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full border border-brand-greyDark shadow-xl bg-white text-brand-blue hover:bg-brand-orange hover:text-white transition-all z-10" />
          </Carousel>

          {/* Mobile indicator / Pagination dots */}
          <div className="flex xl:hidden justify-center gap-2 mt-10">
            {TESTIMONIALS.map((_, i) => (
              <div key={i} className="w-2 h-2 rounded-full bg-brand-blue/10" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
